<template>
  <div class="rescuer-shell">
    <PageLoader :storage="appLoading" />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showSnackBar" color="#427D2D" right :timeout="timeout">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: #fff">{{ msg }}</span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showSnackBar = false">
            <v-icon style="color: #fff">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>

    <v-container fluid class="pa-5">
      <!-- ============ hero ============ -->
      <div class="rescuer-hero">
        <span class="hero-bar"></span>
        <div class="hero-left">
          <div class="hero-avatar" @click="rescueruserdata.photo ? (dialog = true) : null">
            <img
              :src="mediaURL + rescueruserdata.photo"
              v-if="rescueruserdata && rescueruserdata.photo"
              class="avatar-img"
            />
            <v-icon size="36" v-else>mdi-account</v-icon>
          </div>
          <div class="hero-copy">
            <div class="hero-eyebrow">
              <v-icon size="13">mdi-shield-account-outline</v-icon>
              Rescuer Profile
            </div>
            <div class="hero-title">
              {{ (rescueruserdata && rescueruserdata.name) || (rescueruserdata && rescueruserdata.username) || "Rescuer Details" }}
            </div>
            <div class="hero-meta">
              <span class="meta-chip" v-if="rescueruserdata && rescueruserdata.phone">
                <v-icon size="13">mdi-phone-outline</v-icon>
                {{ rescueruserdata.phone }}
              </span>
              <span class="meta-chip" v-if="rescueruserdata && rescueruserdata.email">
                <v-icon size="13">mdi-email-outline</v-icon>
                {{ rescueruserdata.email }}
              </span>
              <span class="meta-chip" v-if="rescuerdata && rescuerdata.district">
                <v-icon size="13">mdi-map-marker-outline</v-icon>
                {{ rescuerdata.district }} District
              </span>
              <span class="meta-chip" v-if="rescueruserdata && rescueruserdata.organization">
                <v-icon size="13">mdi-office-building-outline</v-icon>
                {{ rescueruserdata.organization }}
              </span>
            </div>
          </div>
        </div>
        <div class="hero-right">
          <span :class="['status-chip', rescuerdata && rescuerdata.districtCoordinator ? 'status-ack' : 'status-new']">
            <span class="status-dot"></span>
            {{ rescuerdata && rescuerdata.districtCoordinator ? "District Coordinator" : "Active Rescuer" }}
          </span>
          <div class="hero-actions" v-if="mainRole === 'admin' || mainRole === 'range'">
            <v-btn small depressed class="btn-solid" @click="getPDF()">
              <v-icon small left>mdi-file-pdf-box</v-icon>
              <span class="btnlabel">Download Certificate</span>
            </v-btn>
          </div>
        </div>

        <!-- the summary facts live in the hero rather than in cards of their
             own: four short values never filled a full-width row, they only
             left a lot of empty space under them -->
        <div class="hero-facts">
          <div
            v-for="(fact, i) in heroFacts"
            :key="i"
            :class="['hero-fact', fact.state]"
          >
            <span class="hero-fact-icon"><v-icon size="15">{{ fact.icon }}</v-icon></span>
            <span class="hero-fact-copy">
              <span class="hero-fact-value">{{ fact.value }}</span>
              <span class="hero-fact-label">{{ fact.label }}</span>
            </span>
          </div>
        </div>
      </div>

      <!-- ============ personal & contact details ============ -->
      <div class="panel">
        <div class="panel-head">
          <div class="panel-icon"><v-icon size="19">mdi-account-details-outline</v-icon></div>
          <div class="panel-copy">
            <div class="cardtitle">Personal & Contact Details</div>
            <div class="subtext">Personal identification and contact info</div>
          </div>
        </div>

        <v-layout wrap justify-start>
          <v-flex xs12 sm6 md4 lg3 pa-2 v-for="(field, i) in personalFields" :key="i">
            <div class="detail-cell" v-if="field.value">
              <div class="detail-icon"><v-icon size="15">{{ field.icon }}</v-icon></div>
              <div class="detail-copy">
                <div class="itemKey">{{ field.label }}</div>
                <div class="itemValue">{{ field.value || "—" }}</div>
              </div>
            </div>
          </v-flex>
        </v-layout>
      </div>

      <!-- ============ professional & verification details ============ -->
      <div class="panel">
        <div class="panel-head">
          <div class="panel-icon"><v-icon size="19">mdi-file-certificate-outline</v-icon></div>
          <div class="panel-copy">
            <div class="cardtitle">Verification & Qualification</div>
            <div class="subtext">Identification proofs, background history and applied date</div>
          </div>
        </div>

        <v-layout wrap justify-start>
          <v-flex xs12 sm6 md4 lg3 pa-2 v-for="(field, i) in verificationFields" :key="i">
            <div class="detail-cell" v-if="field.value">
              <div class="detail-icon"><v-icon size="15">{{ field.icon }}</v-icon></div>
              <div class="detail-copy">
                <div class="itemKey">{{ field.label }}</div>
                <div class="itemValue">{{ field.value || "—" }}</div>
              </div>
            </div>
          </v-flex>
        </v-layout>
      </div>

      <!-- ============ address details ============ -->
      <div class="panel" v-if="hasAddress">
        <div class="panel-head">
          <div class="panel-icon"><v-icon size="19">mdi-map-marker-radius-outline</v-icon></div>
          <div class="panel-copy">
            <div class="cardtitle">Address & Jurisdiction</div>
            <div class="subtext">Residential addresses and assigned district jurisdiction</div>
          </div>
        </div>

        <v-layout wrap justify-start>
          <v-flex xs12 sm6 md4 lg3 pa-2 v-for="(field, i) in addressFields" :key="i">
            <div class="detail-cell" v-if="field.value">
              <div class="detail-icon"><v-icon size="15">{{ field.icon }}</v-icon></div>
              <div class="detail-copy">
                <div class="itemKey">{{ field.label }}</div>
                <div class="itemValue">{{ field.value || "—" }}</div>
              </div>
            </div>
          </v-flex>
        </v-layout>
      </div>

      <!-- ============ documents & photos ============ -->
      <div class="panel">
        <div class="panel-head">
          <div class="panel-icon"><v-icon size="19">mdi-folder-account-outline</v-icon></div>
          <div class="panel-copy">
            <div class="cardtitle">Documents & Uploads</div>
            <div class="subtext">Uploaded photograph and identity proof images</div>
          </div>
        </div>

        <v-layout wrap justify-start>
          <v-flex xs12 sm6 md4 pa-2 v-if="rescueruserdata && rescueruserdata.photo">
            <div class="doc-card">
              <div class="doc-title">
                <v-icon size="16">mdi-account-box-outline</v-icon>
                <span>Profile Photo</span>
              </div>
              <div class="doc-preview" @click="dialog = true">
                <img :src="mediaURL + rescueruserdata.photo" class="doc-img clickable" />
              </div>
            </div>
          </v-flex>

          <v-flex xs12 sm6 md4 pa-2 v-if="rescuerdata && rescuerdata.adhar">
            <div class="doc-card">
              <div class="doc-title">
                <v-icon size="16">mdi-card-account-details-outline</v-icon>
                <span>Aadhar Card Image</span>
              </div>
              <div class="doc-preview" @click="dialogadhar = true">
                <img :src="mediaURL + rescuerdata.adhar" class="doc-img clickable" />
              </div>
            </div>
          </v-flex>
        </v-layout>
      </div>
    </v-container>

    <!-- Dialogs -->
    <v-dialog v-model="dialog" max-width="700">
      <v-card rounded="lg">
        <div class="dialog-title">
          <v-icon size="19">mdi-account-box-outline</v-icon>
          <span class="cardtitle">Profile Photo</span>
        </div>
        <v-card-text class="pa-4 text-center">
          <img :src="mediaURL + rescueruserdata.photo" style="max-width: 100%; border-radius: 12px;" />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="#FF1313" @click="dialog = false">
            <span class="btntext">Close</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogadhar" max-width="700">
      <v-card rounded="lg">
        <div class="dialog-title">
          <v-icon size="19">mdi-card-account-details-outline</v-icon>
          <span class="cardtitle">Aadhar Card Image</span>
        </div>
        <v-card-text class="pa-4 text-center">
          <img :src="mediaURL + rescuerdata.adhar" style="max-width: 100%; border-radius: 12px;" />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="#FF1313" @click="dialogadhar = false">
            <span class="btntext">Close</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="approveDialog" max-width="500">
      <v-card rounded="lg">
        <div class="dialog-title">
          <v-icon size="19">mdi-certificate-outline</v-icon>
          <span class="cardtitle">Renew Certificate</span>
        </div>
        <v-card-text class="pt-3">
          <div class="itemKey pb-1">Name</div>
          <v-text-field outlined dense readonly v-model="rescueruserdata.name" hide-details class="pb-3"></v-text-field>

          <div class="itemKey pb-1">Applied Date</div>
          <v-text-field outlined dense readonly v-model="formattedAppliedDate" hide-details></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="#FF1313" @click="approveDialog = false">
            <span class="btntext">Cancel</span>
          </v-btn>
          <v-btn depressed class="btn-solid" @click="handlesubmit()">
            <span class="btnlabel">Renew Certificate</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showSnackBar: false,
      timeout: 5000,
      msg: null,
      rescuerdata: {},
      rescueruserdata: {},
      dialog: false,
      dialogadhar: false,
      approveDialog: false,
      rejectDialog: false,
      trainingDialog: false,
      remarks: "",
      licencenum: "",
      categoryitems: ["Official", "Public"],
      districtCoordinator: "",
      category: "",
      districtCoordinatoritems: ["Yes", "No"],
    };
  },
  beforeMount() {
    this.getData();
  },
  computed: {
    formattedAppliedDate() {
      return this.rescuerdata && this.rescuerdata.applieddate ? this.formatDate(this.rescuerdata.applieddate) : "—";
    },
    mainRole() {
      return this.$store.state.userRole;
    },
    /*
      The four summary facts that sit along the bottom of the hero. They are
      profile attributes, not measurements, so each is a value over a short
      caption. Keeping them out of the stat tile also keeps a year out of its
      thousands separator, where 2023 came out as "2,023".

      Coordinator status is not among them on purpose - the status chip in this
      same hero already says it.
    */
    heroFacts() {
      var d = this.rescuerdata || {};

      //the year on its own says little; the span since is the telling part, so
      //it earns the caption line rather than repeating "rescue started at"
      var startedAt = d.rescueStartedAt
        ? String(d.rescueStartedAt).trim()
        : null;
      var startedLabel = "Rescue started";
      var year = parseInt(startedAt, 10);
      var thisYear = new Date().getFullYear();
      if (!isNaN(year) && year > 1900 && year <= thisYear) {
        var years = thisYear - year;
        startedLabel =
          years <= 0
            ? "Started this year"
            : years + (years === 1 ? " yr" : " yrs") + " of rescue";
      }

      var applied = d.applieddate ? this.formatDate(d.applieddate) : null;

      return [
        {
          label: "Blood group",
          value: d.bloodGroup || "Not stated",
          icon: "mdi-water-outline",
          state: d.bloodGroup ? "" : "is-empty",
        },
        {
          label: startedLabel,
          value: startedAt || "Not stated",
          icon: "mdi-calendar-clock",
          state: startedAt ? "" : "is-empty",
        },
        {
          label: "District",
          value: d.district || "Not stated",
          icon: "mdi-map-marker-outline",
          state: d.district ? "" : "is-empty",
        },
        {
          label: "Registered on",
          value: applied || "Not stated",
          icon: "mdi-calendar-check-outline",
          state: applied ? "" : "is-empty",
        },
      ];
    },
    personalFields() {
      var u = this.rescueruserdata || {};
      var d = this.rescuerdata || {};
      return [
        { label: "Full Name", value: u.name, icon: "mdi-account-outline" },
        { label: "Username", value: u.username, icon: "mdi-account-box-outline" },
        { label: "Phone", value: u.phone, icon: "mdi-phone-outline" },
        { label: "Email", value: u.email, icon: "mdi-email-outline" },
        { label: "Date of Birth", value: this.formatDate(u.dob), icon: "mdi-calendar-text-outline" },
        { label: "Blood Group", value: d.bloodGroup, icon: "mdi-water-outline" },
        { label: "Education", value: d.education, icon: "mdi-school-outline" },
        { label: "Employment", value: d.employment, icon: "mdi-briefcase-outline" },
        { label: "Organization", value: u.organization, icon: "mdi-office-building-outline" },
      ];
    },
    verificationFields() {
      var d = this.rescuerdata || {};
      return [
        { label: "Aadhar Number", value: d.adharnumber, icon: "mdi-card-account-details-outline" },
        { label: "Rescue Started At", value: d.rescueStartedAt ? String(d.rescueStartedAt) : null, icon: "mdi-clock-outline" },
        { label: "Applied Date", value: this.formatDate(d.applieddate), icon: "mdi-calendar-clock-outline" },
        { label: "District Coordinator", value: d.districtCoordinator === true ? "Yes" : (d.districtCoordinator === false ? "No" : null), icon: "mdi-star-outline" },
        { label: "Any Offense History", value: d.offence === true ? "Yes (" + (d.offenceDetails || "No details") + ")" : (d.offence === false ? "No" : null), icon: "mdi-shield-alert-outline" },
        { label: "Disease History", value: d.diseaseStatus === true ? "Yes (" + (d.diseaseDetails || "No details") + ")" : (d.diseaseStatus === false ? "No" : null), icon: "mdi-hospital-box-outline" },
        { label: "Circle Recommendations", value: d.circleRecommendations, icon: "mdi-comment-text-outline" },
      ];
    },
    addressFields() {
      var d = this.rescuerdata || {};
      return [
        { label: "Present Address", value: d.presentAddress, icon: "mdi-map-marker-outline" },
        { label: "Permanent Address", value: d.permanentAddress, icon: "mdi-home-outline" },
        { label: "District", value: d.district, icon: "mdi-compass-outline" },
        { label: "Division", value: d.division, icon: "mdi-nature-people" },
        { label: "Range", value: d.range, icon: "mdi-tree-outline" },
        { label: "Station", value: d.station, icon: "mdi-storefront-outline" },
      ];
    },
    hasAddress() {
      var d = this.rescuerdata || {};
      return !!(d.presentAddress || d.permanentAddress || d.district || d.division || d.range || d.station);
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return "—";
      const [year, month, day] = date.slice(0, 10).split("-");
      return `${day}-${month}-${year}`;
    },
    openEditDialog(item) {
      this.$router.push({
        path: "/edituser",
        query: {
          id: item._id,
        },
      });
    },
    getData() {
      this.appLoading = true;
      axios({
        method: "GET",
        url: "/rescuer/get",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          id: this.$route.query.id,
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            this.rescuerdata = response.data.data || {};
            this.rescueruserdata = (response.data.data && response.data.data.user) || {};
            this.appLoading = false;
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    updatepassword() {
      if (!this.password) {
        this.msg = "Please enter password to update.";
        this.showSnackBar = true;
        return;
      }
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/user/editpassword",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          id: this.$route.query.id,
          password: this.password,
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            this.msg = "Password Updated Successfully.";
            this.showSnackBar = true;
            this.appLoading = false;
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    addTraining() {
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/rescuer/movetotraining/",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          id: this.$route.query.id,
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            this.msg = "Moved to Training List.";
            this.showSnackBar = true;
            this.appLoading = false;
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    rejectUser() {
      if (!this.remarks) {
        this.msg = "Please provide remarks.";
        this.showSnackBar = true;
        return;
      }
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/rescuer/rejectrescuer/",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          id: this.$route.query.id,
          reason: this.remarks,
        },
      })
        .then((response) => {
          if (response.data.status == true) {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.appLoading = false;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    getPDF() {
      this.appLoading = true;
      axios({
        method: "GET",
        url: "/download/certificate/rescuer",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        responseType: "blob",
        params: {
          rescuerId: this.$route.query.id,
        },
      })
        .then((response) => {
          this.appLoading = false;
          const url = URL.createObjectURL(
            new Blob([response.data], {
              type: "application/pdf",
            })
          );
          window.open(url, "_blank");
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          this.msg = "An error occurred while generating the PDF.";
          this.showSnackBar = true;
          console.log(err);
        });
    },
  },
};
</script>

<style scoped>
.rescuer-shell {
  --accent: #10b981;
  --ink: #047857;
  --tint: #f0fdf9;
  --tint-line: #d2efe3;
  --badge: #d1fae5;
  --line: #e7eef3;
  --muted: #64748b;
  --shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px -12px rgba(15, 23, 42, 0.1);

  background: #f6f8fb;
  min-height: 100vh;
  padding-bottom: 2rem;
}

.cardtitle {
  font-family: poppinssemibold, poppinsregular, sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #1a2e23;
  letter-spacing: -0.1px;
}
.subtext {
  font-family: poppinsregular, sans-serif;
  font-size: 12.5px;
  color: var(--muted);
  line-height: 1.45;
}
.itemKey {
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #8496a5;
}
.itemValue {
  font-family: poppinsregular, sans-serif;
  font-size: 13.5px;
  color: #1b2a17;
  line-height: 1.4;
  word-break: break-word;
}
.btnlabel {
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.2px;
}
.btntext {
  font-family: poppinsregular, sans-serif;
  font-size: 11.5px;
}
.btn-solid.v-btn {
  border-radius: 20px !important;
  background-color: #047857 !important;
  background: linear-gradient(135deg, var(--accent, #10b981) 0%, var(--ink, #047857) 100%) !important;
  box-shadow: 0 4px 12px -4px rgba(5, 150, 105, 0.45) !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  transition: filter 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease;
}
.btn-solid.v-btn,
.btn-solid.v-btn .btnlabel,
.btn-solid.v-btn .v-icon {
  color: #ffffff !important;
}
.btn-solid.v-btn:hover {
  filter: brightness(1.08);
  box-shadow: 0 6px 16px -4px rgba(5, 150, 105, 0.5) !important;
  transform: translateY(-1px);
}
.btn-ghost.v-btn {
  border-radius: 20px !important;
  background: #ffffff !important;
  border: 1px solid var(--tint-line, #d2efe3) !important;
  box-shadow: none !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  transition: background 0.25s ease, border-color 0.25s ease;
}
.btn-ghost.v-btn,
.btn-ghost.v-btn .btnlabel,
.btn-ghost.v-btn .v-icon {
  color: var(--ink, #047857) !important;
}
.btn-ghost.v-btn:hover {
  background: var(--tint, #f0fdf9) !important;
  border-color: var(--accent, #10b981) !important;
}

/* ================= hero ================= */
.rescuer-hero {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  padding: 22px 26px 22px 30px;
  border: 1px solid var(--tint-line);
  border-radius: 18px;
  background: linear-gradient(135deg, #ffffff 0%, var(--tint) 100%);
  box-shadow: var(--shadow);
}
.hero-bar {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 5px;
  background: linear-gradient(180deg, var(--accent) 0%, var(--ink) 100%);
}
.hero-left {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  min-width: 0;
  flex: 1 1 340px;
}
.hero-avatar {
  flex: 0 0 auto;
  width: 64px;
  height: 64px;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--accent) 0%, var(--ink) 100%);
  box-shadow: 0 6px 18px -6px rgba(5, 150, 105, 0.55);
  cursor: pointer;
}
.hero-avatar .avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.hero-avatar .v-icon {
  color: #ffffff !important;
}
.hero-copy {
  min-width: 0;
}
.hero-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--ink);
}
.hero-eyebrow .v-icon {
  color: var(--accent) !important;
}
.hero-title {
  margin-top: 2px;
  font-family: poppinssemibold, poppinsregular, sans-serif;
  font-size: 25px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.6px;
  color: #14251c;
  word-break: break-word;
}
.hero-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}
.meta-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 11px;
  border: 1px solid var(--tint-line);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.75);
  font-family: poppinsregular, sans-serif;
  font-size: 11.5px;
  color: #45564d;
  white-space: nowrap;
}
.meta-chip .v-icon {
  color: var(--accent) !important;
}
.hero-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  flex: 0 0 auto;
}
.hero-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}
.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border-radius: 16px;
  padding: 5px 14px;
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.3px;
  white-space: nowrap;
}
.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}
.status-new {
  background: #fdecea;
  color: #c62828;
  box-shadow: 0 0 0 3px rgba(198, 40, 40, 0.08);
}
.status-ack {
  background: #e8f5e9;
  color: #2e7d32;
  box-shadow: 0 0 0 3px rgba(46, 125, 50, 0.08);
}

/* ================= hero summary facts =================
   Four short values never filled a row of cards, so they run along the bottom
   of the hero instead: value over caption, divided off by a hairline.
   flex-basis 100% is what drops them onto their own line inside the hero's
   wrapping flex row. */
.hero-facts {
  flex: 1 1 100%;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-top: 2px;
  padding-top: 16px;
  border-top: 1px solid var(--tint-line);
}
.hero-fact {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.hero-fact-icon {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--tint-line);
  background: #ffffff;
}
.hero-fact-icon .v-icon {
  color: var(--ink) !important;
}
.hero-fact-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.hero-fact-value {
  font-family: poppinssemibold, poppinsregular, sans-serif;
  font-size: 16px;
  line-height: 1.2;
  letter-spacing: -0.2px;
  color: #1a2e23;
  word-break: break-word;
}
.hero-fact-label {
  margin-top: 2px;
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #8496a5;
}
/* a missing value should not shout as loudly as a real one */
.hero-fact.is-empty .hero-fact-value {
  font-size: 14px;
  color: #a7b6c2;
}

@media (max-width: 1100px) {
  .hero-facts {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    row-gap: 15px;
  }
}
@media (max-width: 560px) {
  .hero-facts {
    grid-template-columns: minmax(0, 1fr);
    row-gap: 15px;
  }
}

/* ================= panels ================= */
.panel {
  margin-top: 20px;
  padding: 22px 24px 24px 24px;
  border: 1px solid var(--line);
  border-radius: 18px;
  background: #ffffff;
  box-shadow: var(--shadow);
}
.panel-head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding-bottom: 16px;
  margin-bottom: 6px;
  border-bottom: 1px solid var(--line);
}
.panel-icon {
  flex: 0 0 auto;
  width: 38px;
  height: 38px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--badge);
}
.panel-icon .v-icon {
  color: var(--ink) !important;
}
.panel-copy {
  flex: 1 1 auto;
  min-width: 0;
  padding-top: 2px;
}

/* ================= detail grid ================= */
.detail-cell {
  display: flex;
  align-items: flex-start;
  gap: 11px;
  height: 100%;
  padding: 12px 14px;
  border: 1px solid #edf2f6;
  border-radius: 12px;
  background: #fbfdfe;
  transition: border-color 0.25s ease, background 0.25s ease;
}
.detail-cell:hover {
  border-color: var(--tint-line);
  background: var(--tint);
}
.detail-icon {
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--badge);
}
.detail-icon .v-icon {
  color: var(--ink) !important;
}
.detail-copy {
  min-width: 0;
}
.detail-copy .itemValue {
  margin-top: 3px;
}

/* ================= doc card ================= */
.doc-card {
  padding: 14px 16px;
  border: 1px solid #edf2f6;
  border-radius: 14px;
  background: #fbfdfe;
  transition: border-color 0.25s ease, box-shadow 0.25s ease;
}
.doc-card:hover {
  border-color: var(--tint-line);
  box-shadow: 0 8px 20px -14px rgba(15, 23, 42, 0.35);
}
.doc-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: poppinsmedium, poppinsregular, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #1b2a17;
  margin-bottom: 10px;
}
.doc-title .v-icon {
  color: var(--ink) !important;
}
.doc-preview {
  height: 140px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid var(--line);
  background: #ffffff;
}
.doc-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}
.doc-img:hover {
  transform: scale(1.03);
}
.clickable {
  cursor: pointer;
}

/* ================= dialogs ================= */
.dialog-title {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 22px 14px 22px;
  border-bottom: 1px solid var(--line);
}
.dialog-title .v-icon {
  color: var(--ink) !important;
}

/* ================= responsive ================= */
@media (max-width: 700px) {
  .rescuer-hero {
    padding: 18px 18px 18px 22px;
  }
  .hero-right {
    align-items: flex-start;
    width: 100%;
  }
  .hero-actions {
    justify-content: flex-start;
  }
  .hero-title {
    font-size: 21px;
  }
  .panel {
    padding: 18px 16px 20px 16px;
  }
}
</style>

<style>
.v-dialog,
.v-dialog__content {
  --accent: #10b981;
  --ink: #047857;
  --tint: #f0fdf9;
  --tint-line: #d2efe3;
  --badge: #d1fae5;
  --line: #e7eef3;
  --muted: #64748b;
  --shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px -12px rgba(15, 23, 42, 0.1);
}
</style>